// ============================================================
// caseSectionGroups.ts
// Shared section → fields grouping for the case, used by the PDF
// summary export (which sections to include + how to render each).
// Field KEYS are the flat case column names (the flattenCase key set
// in useAutoSave.ts / the RawCase shape). Each field carries a human
// LABEL so the PDF reads as a form, not raw column names.
//
// summaryKeys = the headline subset shown at detailLevel 'summary';
// 'full' shows every field in the group.
// ============================================================

export interface FieldDef {
  key: string
  label: string
  // long = render full-width (description/notes), not a two-column row.
  long?: boolean
  // copyOnly = a CONTROL field (e.g. a "which input shows" mode flag). It's
  // part of the group so the DUPLICATE copies it (preserving the source's
  // mode), but it's NOT rendered as a PDF row (a raw mode value like
  // "freetext" isn't meaningful case content).
  copyOnly?: boolean
}

export interface SectionGroup {
  key: string
  label: string
  fields: FieldDef[]
  summaryKeys: string[]
}

export const CASE_SECTION_GROUPS: readonly SectionGroup[] = [
  {
    key: 'client',
    label: 'Client',
    summaryKeys: [
      'clientForename', 'clientSurname', 'clientDateOfBirth',
      'clientMainContactNumber', 'clientEmailAddress',
    ],
    fields: [
      // Source
      { key: 'clientSource', label: 'Client source' },
      { key: 'clientSourceContactNumber', label: 'Source contact number' },
      { key: 'clientSourceEmailAddress', label: 'Source email' },
      { key: 'clientSourceAddressLine1', label: 'Source address line 1' },
      { key: 'clientSourceAddressLine2', label: 'Source address line 2' },
      { key: 'clientSourceAddressTown', label: 'Source town' },
      { key: 'clientSourceAddressCounty', label: 'Source county' },
      { key: 'clientSourceAddressPostcode', label: 'Source postcode' },
      // Control flag — whether client source applies. copyOnly (duplicate
      // preserves it; not a PDF row).
      { key: 'clientSourceNotApplicable', label: 'Source not applicable', copyOnly: true },
      // Person
      { key: 'clientTitle', label: 'Title' },
      { key: 'clientForename', label: 'Forename' },
      { key: 'clientSurname', label: 'Surname' },
      { key: 'clientDateOfBirth', label: 'Date of birth' },
      { key: 'clientAddressLine1', label: 'Address line 1' },
      { key: 'clientAddressLine2', label: 'Address line 2' },
      { key: 'clientAddressTown', label: 'Town' },
      { key: 'clientAddressCounty', label: 'County' },
      { key: 'clientAddressPostcode', label: 'Postcode' },
      { key: 'clientHasNiNumber', label: 'Has NI number' },
      { key: 'clientNiNumber', label: 'NI number' },
      { key: 'clientMainContactNumber', label: 'Main contact number' },
      { key: 'clientEmailAddress', label: 'Email' },
      { key: 'clientEmploymentStatus', label: 'Employment status' },
      { key: 'clientOccupation', label: 'Occupation' },
      // GP
      { key: 'clientGpName', label: 'GP name' },
      { key: 'clientGpAddressLine1', label: 'GP address line 1' },
      { key: 'clientGpAddressLine2', label: 'GP address line 2' },
      { key: 'clientGpAddressTown', label: 'GP town' },
      { key: 'clientGpAddressCounty', label: 'GP county' },
      { key: 'clientGpAddressPostcode', label: 'GP postcode' },
      { key: 'clientGpPhone', label: 'GP phone' },
      // Hospital
      { key: 'clientHospitalName', label: 'Hospital name' },
      { key: 'clientHospitalAddressLine1', label: 'Hospital address line 1' },
      { key: 'clientHospitalAddressLine2', label: 'Hospital address line 2' },
      { key: 'clientHospitalAddressTown', label: 'Hospital town' },
      { key: 'clientHospitalAddressCounty', label: 'Hospital county' },
      { key: 'clientHospitalAddressPostcode', label: 'Hospital postcode' },
      { key: 'clientHospitalPhone', label: 'Hospital phone' },
      // Incident
      { key: 'clientTimeOffWork', label: 'Time off work' },
      { key: 'clientDaysOffWork', label: 'Days off work' },
      { key: 'clientDescriptionOfInjuries', label: 'Description of injuries', long: true },
      { key: 'clientMedicalAttention', label: 'Medical attention' },
    ],
  },
  {
    key: 'accident',
    label: 'Accident',
    summaryKeys: ['accidentDate', 'accidentLocation', 'accidentDescription'],
    fields: [
      { key: 'accidentDate', label: 'Accident date' },
      { key: 'accidentTime', label: 'Accident time' },
      { key: 'accidentLocation', label: 'Location' },
      // Control flag — which location input the case uses (address lookup vs
      // free text). copyOnly so the duplicate preserves the mode; not a PDF row.
      { key: 'accidentLocationMode', label: 'Location mode', copyOnly: true },
      { key: 'accidentLocationFreeText', label: 'Location (free text)' },
      { key: 'accidentLocationAddressLine1', label: 'Location address line 1' },
      { key: 'accidentLocationAddressLine2', label: 'Location address line 2' },
      { key: 'accidentLocationAddressTown', label: 'Location town' },
      { key: 'accidentLocationAddressCounty', label: 'Location county' },
      { key: 'accidentLocationAddressPostcode', label: 'Location postcode' },
      { key: 'accidentDescription', label: 'Description', long: true },
      { key: 'accidentDescriptionOther', label: 'Description (other)', long: true },
      { key: 'vehiclePosition', label: 'Vehicle position' },
      { key: 'driverName', label: 'Driver name' },
      { key: 'claimantReg', label: 'Claimant vehicle reg' },
      { key: 'claimantVehicleResponsible', label: 'Claimant vehicle responsible' },
      { key: 'wearingSeatbelt', label: 'Wearing seatbelt' },
      { key: 'numberOfPeopleInVehicle', label: 'People in vehicle' },
      { key: 'policeInvolvement', label: 'Police involvement' },
      { key: 'askCueReference', label: 'AskCue reference' },
    ],
  },
  {
    key: 'injuries',
    label: 'Injuries',
    // Injuries are compact booleans — the summary shows them all.
    summaryKeys: [
      'injuryNeck', 'injuryShoulder', 'injuryBack', 'injuryRibsChestTorso',
      'injuryFaceCheckboneJawNose', 'injuryForearmWristHandFingers',
      'injuryDamageToTeeth', 'injuryHeadSenses', 'injuryHipsPelvisGenitals',
      'injuryLegKneeAnkleFootToe', 'injuryPsychological',
      'injuryHearingEars', 'injuryLungsBreathing', 'injurySkin', 'injuryInternalOrgans', 'injuryFatal', 'injuryOther',
      // (B3b) The "Other" injury's description, so a summary says what it is.
      'injuryOtherDetails',
      'injuryExceptionallySevere',
    ],
    fields: [
      { key: 'injuryNeck', label: 'Neck' },
      { key: 'injuryShoulder', label: 'Shoulder' },
      { key: 'injuryBack', label: 'Back' },
      { key: 'injuryRibsChestTorso', label: 'Ribs / chest / torso' },
      { key: 'injuryFaceCheckboneJawNose', label: 'Face / cheekbone / jaw / nose' },
      { key: 'injuryForearmWristHandFingers', label: 'Forearm / wrist / hand / fingers' },
      { key: 'injuryDamageToTeeth', label: 'Damage to teeth' },
      { key: 'injuryHeadSenses', label: 'Head / senses' },
      { key: 'injuryHipsPelvisGenitals', label: 'Hips / pelvis / genitals' },
      { key: 'injuryLegKneeAnkleFootToe', label: 'Leg / knee / ankle / foot / toe' },
      { key: 'injuryPsychological', label: 'Psychological' },
      { key: 'injuryHearingEars', label: 'Hearing / ears' },
      { key: 'injuryLungsBreathing', label: 'Lungs / breathing' },
      { key: 'injurySkin', label: 'Skin' },
      { key: 'injuryInternalOrgans', label: 'Internal organs' },
      { key: 'injuryFatal', label: 'Fatal injuries (death)' },
      { key: 'injuryOther', label: 'Other (describe)' },
      { key: 'injuryOtherDetails', label: 'Other injury details', long: true },
      { key: 'injuryExceptionallySevere', label: 'Exceptionally severe' },
      { key: 'injuryExceptionallySevereDetails', label: 'Exceptionally severe details', long: true },
    ],
  },
  {
    key: 'defendant',
    label: 'Defendant',
    summaryKeys: ['defendantName', 'defendantReg', 'defendantMainContactNumber'],
    fields: [
      { key: 'defendantName', label: 'Name' },
      { key: 'defendantReg', label: 'Vehicle reg' },
      { key: 'defendantMainContactNumber', label: 'Contact number' },
      { key: 'defendantAddressLine1', label: 'Address line 1' },
      { key: 'defendantAddressLine2', label: 'Address line 2' },
      { key: 'defendantAddressTown', label: 'Town' },
      { key: 'defendantAddressCounty', label: 'County' },
      { key: 'defendantAddressPostcode', label: 'Postcode' },
    ],
  },
  {
    key: 'medical',
    label: 'Medical & third parties',
    summaryKeys: ['medicalAgencyName', 'medicalExpertName', 'tpsName', 'tpiInsurer'],
    fields: [
      // Medical agency
      { key: 'medicalAgencyName', label: 'Medical agency' },
      { key: 'medicalAgencyPhone', label: 'Agency phone' },
      { key: 'medicalAgencyEmail', label: 'Agency email' },
      { key: 'medicalAgencyNotes', label: 'Agency notes', long: true },
      // Medical expert
      { key: 'medicalExpertName', label: 'Medical expert' },
      { key: 'medicalExpertPhone', label: 'Expert phone' },
      { key: 'medicalExpertEmail', label: 'Expert email' },
      { key: 'medicalExpertNotes', label: 'Expert notes', long: true },
      // Third-party solicitor
      { key: 'tpsName', label: 'Third-party solicitor' },
      { key: 'tpsPhone', label: 'TPS phone' },
      { key: 'tpsEmail', label: 'TPS email' },
      { key: 'tpsNotes', label: 'TPS notes', long: true },
      // TPI (insurer)
      { key: 'tpiInsurer', label: 'TPI insurer' },
      { key: 'tpiReferenceNumber', label: 'TPI reference' },
    ],
  },
  {
    key: 'funding',
    label: 'Funding',
    summaryKeys: ['deductionMode', 'ateProvider'],
    fields: [
      { key: 'deductionMode', label: 'Deduction mode' },
      { key: 'deductionCustomValue', label: 'Deduction custom value' },
      { key: 'ateProvider', label: 'ATE provider' },
    ],
  },
]

// Default-on sections in the export dialog (the core PI record). Medical +
// Funding default OFF — included on demand.
export const DEFAULT_SECTION_KEYS: readonly string[] = ['client', 'accident', 'injuries', 'defendant']
